import { hasRole, subscriptionTypeIncludesAcademy } from '@court-sync/shared';
import { useSubscription } from '../hooks/useSubscription';
import { useAuthStore } from '../store/auth';

/**
 * Hook to check if user has active subscription or free trial
 * Returns both the check result and a message for tooltips
 */
export function useSubscriptionCheck() {
  const { user } = useAuthStore();
  const { hasActiveSubscription, hasAcademyOwnerSubscription, isTrialExpired, subscription } = useSubscription();

  const isAcademyOwner = hasRole(user, 'academy_owner');
  /** Delegated admins have no personal Stripe plan; access follows the academy owner's subscription (enforced server-side). */
  const isAcademyAdminOnly = hasRole(user, 'academy_admin') && !isAcademyOwner;
  const hasValidSubscription = isAcademyOwner
    ? hasAcademyOwnerSubscription
    : isAcademyAdminOnly
      ? true
      : hasActiveSubscription;

  // Generate appropriate message based on subscription status
  let tooltipMessage = '';
  if (!hasValidSubscription) {
    const status = subscription?.status;
    
    if (!subscription) {
      tooltipMessage = 'An active subscription or free trial is required to use this feature';
    } else if (isTrialExpired) {
      tooltipMessage = 'Your free trial has expired. Please subscribe to continue using this feature';
    } else if (status === 'canceled') {
      tooltipMessage = 'Your subscription has been cancelled. Please reactivate to use this feature';
    } else if (status === 'past_due') {
      tooltipMessage = 'Your subscription payment is past due. Please update your payment method';
    } else if (status === 'unpaid') {
      tooltipMessage = 'Your subscription payment failed. Please update your payment method';
    } else if (isAcademyOwner && !subscriptionTypeIncludesAcademy(subscription.subscriptionType)) {
      tooltipMessage = 'You need an academy owner subscription to access this feature';
    } else {
      tooltipMessage = 'An active subscription or free trial is required to use this feature';
    }
  }

  return {
    hasValidSubscription,
    tooltipMessage,
    isAcademyOwner,
  };
}

